import React from 'react';
import Hero from '../components/Hero';
import WhySection from '../components/WhySection';
import Features from '../components/Features';
import HowItWorks from '../components/HowItWorks';
import TargetGroups from '../components/TargetGroups';
import TrustPartners from '../components/TrustPartners';
import DownloadSection from '../components/DownloadSection';
import FAQ from '../components/FAQ';
import StickyCTA from '../components/StickyCTA';
import { Page } from '../types';

interface HomeProps {
  navigate: (page: Page) => void;
}

const Home: React.FC<HomeProps> = ({ navigate }) => {
  return (
    <>
      <section id="hero">
        <Hero navigate={navigate} />
      </section>
      
      <section id="warum" className="py-24 bg-neutral-light border-y border-gray-100">
        <WhySection />
      </section>
      
      <section id="funktionen" className="py-24">
        <Features navigate={navigate} />
      </section>

      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
           <h3 className="text-3xl font-black text-secondary mb-6">Sei von Anfang an dabei</h3>
           <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
              Sei einer der Ersten, die ErsteHilfe2Go testen dürfen. <br />
              Trage dich jetzt unverbindlich ein.
           </p>
           <button 
             onClick={() => navigate('warteliste')}
             className="bg-primary text-white px-10 py-5 rounded-2xl font-black text-xl hover:scale-105 transition-all shadow-xl active:scale-95"
           >
             Jetzt vormerken lassen
           </button>
           <p className="text-xs text-gray-400 mt-6 font-bold uppercase tracking-widest">
              ✓ Start erfolgt in Kürze
           </p>
        </div>
      </section>

      <section id="anleitung" className="py-24 bg-secondary text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary opacity-5 rounded-full blur-[100px] -mr-48 -mt-48"></div>
        <HowItWorks navigate={navigate} />
      </section>

      <section id="zielgruppen" className="py-24">
        <TargetGroups />
      </section>

      <section id="partner" className="py-24 bg-gray-50 border-y border-gray-100">
        <TrustPartners navigate={navigate} />
      </section>

      {/* SEO MASTER SECTION: 900-1200 words equivalent optimized content */}
<section className="py-24 bg-white">
  <div className="container mx-auto px-4 md:px-6 max-w-5xl">
    <div className="prose prose-lg max-w-none text-gray-600">
      <h2 className="text-3xl md:text-5xl font-black text-secondary mb-10 leading-tight">
        Erste Hilfe lernen in Deutschland: Die digitale Lernplattform für Wissen, Sicherheit und Notfälle
      </h2>

      <div className="space-y-12">
        <div>
          <h3 className="text-2xl font-bold text-secondary mb-4">Was ist ErsteHilfe2Go?</h3>
          <p className="leading-relaxed">
            ErsteHilfe2Go ist mehr als nur eine <strong>Notfall-App</strong> – es ist eine digitale
            <strong> Lernplattform für Erste Hilfe</strong>, mit der du medizinisches Wissen Schritt für Schritt,
            verständlich und ohne Zeitdruck lernen kannst. Unsere Inhalte helfen dir dabei,
            <strong> Erste-Hilfe-Wissen</strong> sicher aufzubauen, zu wiederholen und im Ernstfall besser abzurufen.
            Egal ob du gerade einen <strong>Erste-Hilfe-Kurs</strong> gemacht hast, dein Wissen auffrischen willst
            oder als Einsteiger die wichtigsten Grundlagen lernen möchtest: ErsteHilfe2Go macht
            <strong> Erste Hilfe lernen</strong> einfach, digital und alltagstauglich.
          </p>
        </div>

        <div>
          <h3 className="text-2xl font-bold text-secondary mb-4">Warum Erste-Hilfe-Wissen so wichtig ist</h3>
          <p className="leading-relaxed">
            Notfallsituationen kommen oft plötzlich und ohne Vorwarnung. Viele Menschen haben zwar einmal einen
            <strong> Erste-Hilfe-Kurs</strong> besucht, fühlen sich im Ernstfall aber trotzdem unsicher.
            Genau hier setzt ErsteHilfe2Go an: Wir vermitteln dir medizinisches Basiswissen so, dass du es in Ruhe
            lernen, wiederholen und verstehen kannst. Unser Fokus liegt darauf, dir
            <strong> Erste-Hilfe-Wissen</strong> auf eine klare, sichere und lernfreundliche Weise zugänglich zu machen.
            So kannst du dein Wissen auffrischen, typische Notfälle besser einordnen und im entscheidenden Moment
            ruhiger reagieren.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-xl font-bold text-secondary mb-4">Digitale Erste-Hilfe-App mit Lernfokus</h3>
            <p className="text-sm leading-relaxed">
              Unsere <strong>Erste-Hilfe-App</strong> ist darauf ausgelegt, Wissen verständlich und systematisch zu vermitteln.
              Du lernst wichtige Inhalte wie stabile Seitenlage, Herzdruckmassage, Schlaganfall-Erkennung oder
              den Umgang mit Blutungen in einem Format, das ohne Stress und ohne Druck funktioniert.
              Jede <strong>Erste Hilfe Anleitung</strong> ist klar strukturiert, medizinisch eingeordnet und so formuliert,
              dass auch Laien die Inhalte direkt nachvollziehen können.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-bold text-secondary mb-4">Erste Hilfe online und im eigenen Tempo lernen</h3>
            <p className="text-sm leading-relaxed">
              Mit ErsteHilfe2Go kannst du <strong>Erste Hilfe online lernen</strong> – wann und wo du möchtest.
              Anders als in einem klassischen Kurs lernst du bei uns in deinem eigenen Tempo und kannst Inhalte so oft
              wiederholen, wie du möchtest. Das macht unsere Plattform ideal für alle, die
              <strong> medizinisches Wissen digital</strong> aufbauen oder vorhandenes Wissen sicher auffrischen möchten.
            </p>
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-bold text-secondary mb-4">So unterstützt dich ErsteHilfe2Go im Alltag</h3>
          <p className="leading-relaxed">
            ErsteHilfe2Go begleitet dich nicht nur im akuten Notfall, sondern vor allem beim Lernen und Verstehen.
            Wir vermitteln Inhalte so, dass du Zusammenhänge erkennst, Warnzeichen besser einordnen kannst und dich
            bei den wichtigsten Maßnahmen sicherer fühlst. Ob zu Hause, unterwegs oder zur Vorbereitung auf einen
            praktischen <strong>Erste-Hilfe-Kurs</strong> – unsere Inhalte helfen dir dabei, Schritt für Schritt
            Wissen aufzubauen.
          </p>
          <p className="leading-relaxed mt-4">
            Damit positionieren wir uns bewusst nicht nur als <strong>Notfall-App</strong>, sondern als digitale
            Plattform für <strong>Erste-Hilfe-Wissen</strong>, die Lernen, Wiederholung und Orientierung miteinander verbindet.
            Weitere kompakte Inhalte findest du in unserem <a href="/wissenshub" className="text-primary font-bold hover:underline">Wissenshub </a>
            oder in unserem <a href="/blog" className="text-primary font-bold hover:underline">Blog</a>.
          </p>
        </div>

        <div>
          <h3 className="text-2xl font-bold text-secondary mb-4">Für wen ist ErsteHilfe2Go geeignet?</h3>
          <p className="mb-6">
            Unsere Inhalte sind für alle gedacht, die <strong>Erste Hilfe lernen</strong> oder ihr Wissen auffrischen möchten:
          </p>
          <ul className="list-disc pl-6 space-y-4">
            <li><strong>Erste Hilfe für Laien:</strong> Verständliche Grundlagen ohne medizinisches Vorwissen.</li>
            <li><strong>Erste Hilfe für Eltern:</strong> Wichtige Inhalte zu Kindernotfällen, Verschlucken und typischen Alltagssituationen.</li>
            <li><strong>Erste Hilfe für Fahrschüler:</strong> Digitale Vorbereitung auf den Führerschein und den Alltag als Ersthelfer.</li>
            <li><strong>Erste Hilfe für Beruf und Alltag:</strong> Praktisches Wissen für Schule, Arbeit, Reisen und Freizeit.</li>
          </ul>
        </div>

        <div className="bg-primary/5 p-10 rounded-[3rem] border border-gray-100">
          <h3 className="text-2xl font-bold text-secondary mb-4">Mehr über unsere Funktionen und Inhalte</h3>
          <p className="text-sm leading-relaxed text-secondary/80">
            Wenn du wissen möchtest, wie das digitale Lernen in der App funktioniert, findest du mehr auf unserer Seite
            <a href="/funktion" className="text-primary font-bold hover:underline"> Funktionen</a>.
            Dort zeigen wir dir, wie ErsteHilfe2Go aufgebaut ist und wie du medizinisches Wissen Schritt für Schritt lernen kannst.
            Wenn du frühzeitig dabei sein möchtest, kannst du dich außerdem direkt auf die
            <a href="/warteliste" className="text-primary font-bold hover:underline"> Warteliste</a> setzen lassen.
          </p>
        </div>

        <div className="bg-primary/5 p-10 rounded-[3rem] border border-gray-100">
          <h3 className="text-2xl font-bold text-secondary mb-4">Medizinische Qualität und Vertrauen</h3>
          <p className="text-sm leading-relaxed text-secondary/80 italic">
            Sicherheit basiert auf Vertrauen. Deshalb orientiert sich ErsteHilfe2Go an aktuellen medizinischen
            Empfehlungen und legt großen Wert auf verständlich aufbereitetes, fachlich geprüftes
            <strong> Erste-Hilfe-Wissen</strong>. Unser Ziel ist es, medizinisches Wissen für alle zugänglich zu machen –
            klar, seriös und ohne unnötige Hektik.
          </p>
        </div>
      </div>

      <p className="text-sm text-gray-400 italic bg-gray-50 p-8 rounded-3xl border border-gray-200 mt-12">
        <strong>Wichtiger Hinweis:</strong> ErsteHilfe2Go dient der Wissensvermittlung und kann dich beim Lernen
        und im Notfall unterstützen. Die App ersetzt jedoch niemals den professionellen Rettungsdienst (112)
        oder einen praktischen Erste-Hilfe-Kurs. Im Notfall gilt: immer den Rettungsdienst alarmieren.
      </p>
    </div>
  </div>
</section>

      <section id="warteliste" className="py-24">
        <DownloadSection navigate={navigate} />
      </section>

      <section id="faq" className="py-24 bg-neutral-light">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-extrabold text-secondary">Fragen? Antworten!</h2>
        </div>
        <FAQ showCategories={false} limit={4} />
      </section>
      
      <div className="container mx-auto px-4 md:px-6 pb-20">
         <div className="bg-gray-50 p-8 rounded-3xl border border-gray-200 text-center max-w-4xl mx-auto">
            <p className="text-xs text-gray-400 leading-relaxed">
               <strong>Medizinischer Hinweis:</strong> Diese Website und die zukünftige App dienen ausschließlich der Wissensvermittlung und ersetzen keine medizinische Beratung oder professionelle Hilfe. In akuten Notfällen ist immer der Notruf (z. B. 112) zu wählen. ErsteHilfe2Go ersetzt keinen behördlich vorgeschriebenen Erste-Hilfe-Kurs.
            </p>
         </div>
      </div>

      <StickyCTA navigate={navigate} />
    </>
  );
};

export default Home;